import React, { useMemo, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ConfirmationModal from '../Common/ConfirmationModal';
import { subscriptionPlanApi } from '../../services/subscriberApi';
import { EmptyState, LoadingState } from '../Common/PanelState';
import { MONTH_BASELINE_PRINT, calcSavingsPct, parseFeatures } from '../../utils/planPricing';

const FLARE_RED = '#c4151c';
const FLARE_RED_DARK = '#9f1016';
const Icon = ({ path, className = 'h-5 w-5' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
);
const formatPKR = (n) => `Rs. ${Number(n || 0).toLocaleString('en-PK')}`;

function durationOf(plan) {
  return Number(plan?.durationMonths ?? plan?.duration_months ?? 0);
}

function isPlanActive(plan) {
  if (plan?.is_active === false) return false;
  const status = String(plan?.status || '').toLowerCase();
  if (status === 'inactive' || status === 'draft') return false;
  return true;
}

function StatCard({ label, value, hint, icon, iconBg, valueClass }) {
  return (
    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-lg flex items-center justify-center text-white shadow-sm shrink-0"
          style={{ backgroundColor: iconBg }}
        >
          <Icon path={icon} className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase text-gray-500 tracking-wide">{label}</div>
          <div className={`text-2xl font-extrabold leading-tight ${valueClass || 'text-gray-900'}`}>{value}</div>
          {hint ? <div className="text-xs text-gray-400 mt-0.5">{hint}</div> : null}
        </div>
      </div>
    </div>
  );
}

const EMPTY_PLAN_FORM = {
  name: '',
  slug: '',
  durationMonths: 12,
  price_pkr: 3000,
  description: '',
  featuresText: '',
  includes_physical: true,
  is_active: true,
};

function PlanForm({ planToEdit, onBack }) {
  const [formData, setFormData] = useState(EMPTY_PLAN_FORM);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (planToEdit) {
      setFormData({
        ...EMPTY_PLAN_FORM,
        ...planToEdit,
        durationMonths: durationOf(planToEdit) || 12,
        slug: planToEdit.slug || '',
        featuresText: parseFeatures(planToEdit.features).join('\n'),
        includes_physical: planToEdit.includes_physical !== false,
        is_active: isPlanActive(planToEdit),
      });
    } else {
      setFormData(EMPTY_PLAN_FORM);
    }
  }, [planToEdit]);
  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    const numeric = id === 'price_pkr' || id === 'durationMonths';
    const v = type === 'checkbox' ? checked : (numeric ? Number(value) || 0 : value);
    setFormData((prev) => ({ ...prev, [id]: v }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) return toast.error('Plan name required');
    if (!formData.price_pkr) return toast.error('Plan price required');
    setSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        slug: String(formData.slug || '').trim(),
        duration_months: Number(formData.durationMonths || 1),
        durationMonths: Number(formData.durationMonths || 1),
        price_pkr: Number(formData.price_pkr || 0),
        description: formData.description || '',
        features: parseFeatures(formData.featuresText),
        includes_physical: Boolean(formData.includes_physical),
        is_active: Boolean(formData.is_active),
      };
      if (planToEdit) {
        await subscriptionPlanApi.update(planToEdit.id, payload);
        toast.success('Plan updated — website will show these changes.');
      } else {
        await subscriptionPlanApi.create(payload);
        toast.success('Plan created — it is now live on the website.');
      }
      onBack();
    } catch (err) {
      const msg = err.response?.data?.message
        || (err.response?.data?.errors && Object.values(err.response.data.errors).flat().join(' '))
        || err.message
        || 'Failed to save';
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const months = Math.max(1, Number(formData.durationMonths || 1));
  const perMonth = formatPKR(Math.round(Number(formData.price_pkr || 0) / months));
  const savePct = calcSavingsPct(months, Number(formData.price_pkr || 0));
  const baseline = MONTH_BASELINE_PRINT * months;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <button type="button" onClick={onBack} className="flex items-center gap-2 text-sm font-semibold hover:opacity-80" style={{ color: FLARE_RED }}>
            <Icon path="M10 19l-7-7m0 0l7-7m-7 7h18" className="h-4 w-4" />
            Back to Plans
          </button>
          <h1 className="mt-2 admin-page-title">{planToEdit ? 'Edit Subscription Plan' : 'Add New Subscription Plan'}</h1>
          <p className="admin-page-sub">These fields are what shoppers see on the website. Saving here updates the live plans page.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50">
          <div className="text-sm font-bold text-gray-900">Plan details</div>
          <div className="text-xs text-gray-500 mt-0.5">Name, price, bullets, and visibility — same data the website uses.</div>
        </div>
        <div className="p-6 sm:p-8 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="text-xs font-semibold text-gray-600">Plan Name * <span className="font-normal text-gray-400">(shown on website cards)</span></label>
              <input id="name" value={formData.name} onChange={handleChange} placeholder="e.g. Yearly Printed · Most Popular" className="mt-1 w-full p-3 bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none text-sm" />
            </div>
            <div>
              <label htmlFor="slug" className="text-xs font-semibold text-gray-600">URL slug</label>
              <input id="slug" value={formData.slug} onChange={handleChange} placeholder="printed-yearly-12-months" className="mt-1 w-full p-3 bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none text-sm" />
              <p className="mt-1 text-xs text-gray-400">Leave blank on create to auto-generate from the name.</p>
            </div>
            <div>
              <label htmlFor="durationMonths" className="text-xs font-semibold text-gray-600">Duration (Months) *</label>
              <select id="durationMonths" value={formData.durationMonths} onChange={handleChange} className="mt-1 w-full p-3 bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none text-sm">
                <option value={1}>1 Month (Monthly)</option>
                <option value={3}>3 Months (Quarterly)</option>
                <option value={6}>6 Months (Half-Yearly)</option>
                <option value={12}>12 Months (Yearly)</option>
                <option value={24}>24 Months (Biennial / 2-Year)</option>
              </select>
            </div>
            <div>
              <label htmlFor="price_pkr" className="text-xs font-semibold text-gray-600">Total Price PKR *</label>
              <input id="price_pkr" type="number" min="0" value={formData.price_pkr} onChange={handleChange} className="mt-1 w-full p-3 bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none text-sm" />
              <p className="mt-1 text-xs text-gray-500">
                Per month average: <span className="font-bold text-gray-700">{perMonth}</span>
                {savePct > 0 ? <> · Website save badge: <span className="font-bold text-emerald-700">{savePct}% OFF</span> vs Rs {baseline.toLocaleString('en-PK')}</> : null}
              </p>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50 w-full cursor-pointer">
                <input id="is_active" type="checkbox" checked={!!formData.is_active} onChange={handleChange} className="h-5 w-5 rounded" />
                <span className="text-sm font-semibold text-gray-700">Active &amp; visible on the website</span>
              </label>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50 w-full cursor-pointer">
                <input id="includes_physical" type="checkbox" checked={!!formData.includes_physical} onChange={handleChange} className="h-5 w-5 rounded" />
                <span className="text-sm font-semibold text-gray-700">Includes printed / physical magazine</span>
              </label>
            </div>
          </div>
          <div>
            <label htmlFor="description" className="text-xs font-semibold text-gray-600">Short description</label>
            <textarea id="description" rows="3" value={formData.description || ''} onChange={handleChange} placeholder="Shown under the plan and in admin lists. e.g. Full year of printed Flare. Save 33% — avg Rs 1,000 / month." className="mt-1 w-full p-3 bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none text-sm" />
          </div>
          <div>
            <label htmlFor="featuresText" className="text-xs font-semibold text-gray-600">Feature bullets (one per line) <span className="font-normal text-gray-400">— website plan card checklist</span></label>
            <textarea id="featuresText" rows="7" value={formData.featuresText || ''} onChange={handleChange} placeholder={'12 printed issues — delivered each month\nFree nationwide courier on every issue\nSave 33% vs monthly — avg Rs 1,000 / month'} className="mt-1 w-full p-3 bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none text-sm font-mono" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
          <button type="button" onClick={onBack} disabled={submitting} className="px-5 py-2.5 font-semibold rounded-lg border border-gray-200 hover:bg-white text-gray-700 text-sm bg-white shadow-sm">Cancel</button>
          <button type="submit" disabled={submitting} className="px-8 py-2.5 text-white font-bold rounded-lg shadow-lg disabled:opacity-50 transition-colors" style={{ backgroundColor: FLARE_RED }}>
            {submitting ? 'Saving...' : planToEdit ? 'Save Changes' : 'Create Plan'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function SubscriptionPlansSection() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [view, setView] = useState('list');
  const [editingPlan, setEditingPlan] = useState(null);
  const [deletingPlan, setDeletingPlan] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const loadPlans = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data } = await subscriptionPlanApi.getAll({ per_page: 100 });
      const list = data?.data || data?.plans || (Array.isArray(data) ? data : []) || [];
      setPlans(Array.isArray(list) ? list : []);
      if (data && data.ok === false) {
        setLoadError('Could not reach the server. Showing locally saved plans.');
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Failed to load subscription plans';
      setLoadError(msg);
      setPlans([]);
      toast.error(msg);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadPlans(); }, []);

  const handleDelete = async () => {
    if (!deletingPlan) return;
    try {
      await subscriptionPlanApi.remove(deletingPlan.id);
      toast.success('Plan deleted');
      loadPlans();
    } catch (e) {
      setPlans((prev) => prev.filter((p) => p.id !== deletingPlan.id));
      toast.success('Plan removed (local)');
    } finally { setDeletingPlan(null); }
  };

  const filtered = useMemo(() => {
    let list = plans;
    if (statusFilter === 'active') list = list.filter((p) => isPlanActive(p));
    if (statusFilter === 'inactive') list = list.filter((p) => !isPlanActive(p));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [plans, statusFilter, search]);

  const stats = useMemo(() => {
    const activeCount = plans.filter((p) => isPlanActive(p)).length;
    const totalRevenue = plans.reduce((s, p) => s + (Number(p.price_pkr || 0)), 0);
    const avgDuration = plans.length ? Math.round(plans.reduce((s, p) => s + durationOf(p), 0) / plans.length) : 0;
    return { total: plans.length, active: activeCount, avgDuration, totalRevenue };
  }, [plans]);

  const filtersActive = Boolean(statusFilter !== 'all' || search.trim());
  const clearFilters = () => {
    setStatusFilter('all');
    setSearch('');
  };

  if (view === 'form') return <PlanForm planToEdit={editingPlan} onBack={() => { setEditingPlan(null); setView('list'); loadPlans(); }} />;

  return (
    <div className="space-y-6">
      <ConfirmationModal isOpen={!!deletingPlan} onClose={() => setDeletingPlan(null)} onConfirm={handleDelete} title="Delete Plan">
        Delete plan <strong>{deletingPlan?.name}</strong>? Subscribers on this plan won&apos;t be affected but new sign-ups won&apos;t see it.
      </ConfirmationModal>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="admin-page-title">Subscription Plans</h1>
          <p className="admin-page-sub">Create and manage recurring subscription packages</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={loadPlans}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-800 rounded-lg shadow-sm hover:bg-gray-50 font-semibold text-sm"
          >
            <Icon path="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" className="h-4 w-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => { setEditingPlan(null); setView('form'); }}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-white font-bold rounded-lg shadow-lg"
            style={{ backgroundColor: FLARE_RED }}
          >
            <Icon path="M12 4v16m8-8H4" className="h-5 w-5" />
            New Plan
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Plans"
          value={stats.total}
          hint="All packages"
          icon="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          iconBg={FLARE_RED}
        />
        <StatCard
          label="Active"
          value={stats.active}
          hint="Visible to subscribers"
          icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          iconBg="#059669"
        />
        <StatCard
          label="Avg Duration"
          value={`${stats.avgDuration} mo`}
          hint="Across all plans"
          icon="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          iconBg="#4f46e5"
        />
        <StatCard
          label="Sum of Prices"
          value={formatPKR(stats.totalRevenue)}
          hint="Catalog total"
          icon="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          iconBg="#d97706"
          valueClass="text-gray-900"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-600">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Search</label>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or description…"
              className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold"
            style={{ backgroundColor: '#fff1f2', color: FLARE_RED_DARK, border: '1px solid rgba(196,21,28,0.15)' }}
          >
            <Icon path="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" className="h-3.5 w-3.5" />
            {filtered.length} matching
          </div>
          {filtersActive ? (
            <button
              type="button"
              onClick={clearFilters}
              className="ml-1 px-2.5 py-1 rounded-md bg-white border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Clear filters
            </button>
          ) : null}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-gray-900">Plan list</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {loading ? 'Updating…' : `${filtered.length} ${filtered.length === 1 ? 'plan' : 'plans'} matching`}
            </div>
          </div>
        </div>
        {loading ? (
          <LoadingState label="Loading plans" />
        ) : loadError && plans.length === 0 ? (
          <EmptyState title="Could not load subscription plans" hint={loadError}>
            <button
              type="button"
              onClick={loadPlans}
              className="px-6 py-2.5 rounded-lg text-white font-bold shadow"
              style={{ backgroundColor: FLARE_RED }}
            >
              Try again
            </button>
          </EmptyState>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No subscription plans yet"
            hint={filtersActive
              ? 'Try clearing filters or searching a different name.'
              : 'Create your first plan — e.g. Yearly Digital Rs. 3000/year'}
          >
            {filtersActive ? (
              <button type="button" onClick={clearFilters} className="px-6 py-2.5 rounded-lg bg-white border border-gray-200 text-gray-800 font-bold shadow-sm">Clear filters</button>
            ) : (
              <button type="button" onClick={() => setView('form')} className="px-6 py-2.5 rounded-lg text-white font-bold shadow" style={{ backgroundColor: FLARE_RED }}>Create First Plan</button>
            )}
          </EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold">Plan</th>
                  <th className="px-6 py-3 text-left font-semibold">Duration</th>
                  <th className="px-6 py-3 text-left font-semibold">Price</th>
                  <th className="px-6 py-3 text-left font-semibold">Per/Mo Avg</th>
                  <th className="px-6 py-3 text-left font-semibold">Save</th>
                  <th className="px-6 py-3 text-left font-semibold">Status</th>
                  <th className="px-6 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((p, idx) => {
                  const months = durationOf(p);
                  const active = isPlanActive(p);
                  return (
                    <tr key={p.id} className={`hover:bg-rose-50/30 ${idx % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'}`}>
                      <td className="px-6 py-4">
                        <div className="font-bold text-gray-900">{p.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5 max-w-md truncate">{p.description || '—'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border bg-indigo-50 text-indigo-800 border-indigo-100">
                          {months} {months === 1 ? 'month' : 'months'}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-bold text-gray-900">{formatPKR(p.price_pkr)}</td>
                      <td className="px-6 py-4 text-gray-600">
                        {formatPKR(Math.round(Number(p.price_pkr || 0) / Math.max(1, months || 1)))}
                      </td>
                      <td className="px-6 py-4">
                        {calcSavingsPct(months, Number(p.price_pkr || 0)) > 0 ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border bg-emerald-50 text-emerald-800 border-emerald-100">
                            {calcSavingsPct(months, Number(p.price_pkr || 0))}% OFF
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${active ? 'bg-emerald-50 text-emerald-800 border-emerald-100' : 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                          {active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <button type="button" onClick={() => { setEditingPlan(p); setView('form'); }} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 mr-2">Edit</button>
                        <button type="button" onClick={() => setDeletingPlan(p)} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-50 hover:bg-red-100 text-red-700">Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
